import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, CheckCircle2, Loader2, Send, ShieldQuestion, Check, ChevronDown, ChevronRight, Info } from "lucide-react";
import { toast } from "sonner";
import { lintProposed, LintIssue } from "@/lib/reviewLint";
import { MarkdownBlockEditor } from "./MarkdownBlockEditor";

const RULES_OPEN_KEY = "reviewer_rules_open_v1";

type Doc = {
  doc_id: string;
  title: string;
  version: string;
  body_markdown: string;
  collection?: string | null;
};

export type ReviewerEditHandle = {
  submit: () => Promise<{ submitted: boolean }>;
  getSnapshot: () => { dirty: boolean; hasIssues: boolean; hasProposal: boolean };
};

export const ReviewerEditView = forwardRef<ReviewerEditHandle, {
  doc: Doc;
  onSubmittedForReview?: () => void;
  scrollRef?: React.RefObject<HTMLDivElement>;
}>(function ReviewerEditView({
  doc,
  onSubmittedForReview,
  scrollRef,
}, ref) {
  const base = doc.body_markdown || "";
  const [proposed, setProposed] = useState(base);
  const [note, setNote] = useState("");
  const [proposalId, setProposalId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checkResult, setCheckResult] = useState<any>(null);
  const [proposalStatus, setProposalStatus] = useState<string | null>(null);
  const [rawMode, setRawMode] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [autosaving, setAutosaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loadedDraft, setLoadedDraft] = useState(false);
  const [rulesOpen, setRulesOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(RULES_OPEN_KEY) === "1"; } catch { return false; }
  });
  const savedRef = useRef<{ proposed: string; note: string } | null>(null);
  const proposalIdRef = useRef<string | null>(null);
  const latestRef = useRef({ proposed, note });
  latestRef.current = { proposed, note };

  // When the doc changes (Prev/Next in workbench), reset local state so the
  // editor picks up the new base and its own draft.
  useEffect(() => {
    setProposed(base);
    setNote("");
    setProposalId(null);
    proposalIdRef.current = null;
    setProposalStatus(null);
    setCheckResult(null);
    setLastSavedAt(null);
    setSaveError(null);
    setLoadedDraft(false);
    savedRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.doc_id]);

  function toggleRules() {
    setRulesOpen((v) => {
      const n = !v;
      try { localStorage.setItem(RULES_OPEN_KEY, n ? "1" : "0"); } catch {}
      return n;
    });
  }

  function toggleRawMode() {
    setRawMode((value) => !value);
  }

  function handleRawModeKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      toggleRawMode();
    }
  }

  const issues: LintIssue[] = useMemo(() => lintProposed(base, proposed), [base, proposed]);
  const dirty = proposed.trim() !== base.trim();

  // Load any existing draft/needs_revision proposal by this reviewer for this doc
  useEffect(() => {
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData?.user?.id;
      if (!uid) { setLoadedDraft(true); return; }
      const { data } = await supabase
        .from("doc_edit_proposals" as any)
        .select("id, proposed_body, editor_note, base_version, status, substance_check")
        .eq("doc_id", doc.doc_id)
        .eq("editor_id", uid)
        .in("status", ["draft", "needs_revision", "checked", "escalated"])
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (data) {
        const row = data as any;
        setProposalId(row.id);
        proposalIdRef.current = row.id;
        const body = row.proposed_body ?? base;
        const nt = row.editor_note ?? "";
        setProposed(body);
        setNote(nt);
        setProposalStatus(row.status);
        savedRef.current = { proposed: body, note: nt };
        setLastSavedAt(new Date());
        if (row.substance_check) setCheckResult(row.substance_check);
      }
      setLoadedDraft(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.doc_id]);

  const persistDraft = useCallback(
    async (silent: boolean): Promise<string | null> => {
      if (!silent) setSaving(true);
      else setAutosaving(true);
      try {
        const { data: userData } = await supabase.auth.getUser();
        const uid = userData?.user?.id;
        if (!uid) throw new Error("Not signed in");

        const currentId = proposalIdRef.current;
        const bodyNow = latestRef.current.proposed;
        const noteNow = latestRef.current.note;

        if (currentId) {
          const updatePayload: Record<string, unknown> = {
            proposed_body: bodyNow,
            editor_note: noteNow,
          };
          if (!silent) updatePayload.status = "draft";
          const { error } = await supabase
            .from("doc_edit_proposals" as any)
            .update(updatePayload)
            .eq("id", currentId);
          if (error) throw error;
          savedRef.current = { proposed: bodyNow, note: noteNow };
          setLastSavedAt(new Date());
          setSaveError(null);
          if (!silent) toast.success("Draft saved.");
          return currentId;
        } else {
          const { data, error } = await supabase
            .from("doc_edit_proposals" as any)
            .insert({
              doc_id: doc.doc_id,
              base_version: doc.version,
              proposed_body: bodyNow,
              editor_id: uid,
              editor_note: noteNow,
              status: "draft",
            })
            .select("id")
            .maybeSingle();
          if (error) throw error;
          const id = (data as any)?.id ?? null;
          proposalIdRef.current = id;
          setProposalId(id);
          setProposalStatus("draft");
          savedRef.current = { proposed: bodyNow, note: noteNow };
          setLastSavedAt(new Date());
          setSaveError(null);
          if (!silent) toast.success("Draft saved.");
          return id;
        }
      } catch (e: any) {
        if (!silent) toast.error(e?.message || "Could not save draft.");
        else {
          console.error("[reviewer autosave] failed", e);
          setSaveError(e?.message || "Could not save");
        }
        return null;
      } finally {
        if (!silent) setSaving(false);
        else setAutosaving(false);
      }
    },
    [doc.doc_id, doc.version],
  );

  useEffect(() => {
    if (!loadedDraft) return;
    const saved = savedRef.current;
    if (saved && saved.proposed === proposed && saved.note === note) return;
    if (!proposalIdRef.current && proposed === base && note.trim() === "") return;

    const t = setTimeout(() => {
      persistDraft(true);
    }, 1200);
    return () => clearTimeout(t);
  }, [proposed, note, loadedDraft, base, persistDraft]);

  useEffect(() => {
    const flush = () => {
      const saved = savedRef.current;
      const bodyNow = latestRef.current.proposed;
      const noteNow = latestRef.current.note;
      if (saved && saved.proposed === bodyNow && saved.note === noteNow) return;
      persistDraft(true);
    };
    const onVis = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("beforeunload", flush);
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("beforeunload", flush);
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [persistDraft]);

  async function submitForCheck(): Promise<{ submitted: boolean }> {
    if (issues.length > 0) {
      toast.error("Please resolve checks before sending.");
      return { submitted: false };
    }
    if (!dirty) {
      toast.error("No changes to send.");
      return { submitted: false };
    }
    setSubmitting(true);
    try {
      let id = proposalId;
      if (!id) {
        id = await persistDraft(true);
        if (!id) return { submitted: false };
      } else {
        await supabase
          .from("doc_edit_proposals" as any)
          .update({ proposed_body: proposed, editor_note: note, status: "submitted" })
          .eq("id", id);
        savedRef.current = { proposed, note };
      }

      const { data, error } = await supabase.functions.invoke("check-edit", {
        body: { proposal_id: id },
      });
      if (error) throw error;
      setCheckResult((data as any)?.substance_check ?? data);
      const d = data as any;
      if (d?.status) setProposalStatus(d.status);
      if (d?.status === "approved") {
        toast.success(
          d?.published_version
            ? `Auto-approved and published as v${d.published_version}.`
            : "Auto-approved and published.",
        );
        onSubmittedForReview?.();
        return { submitted: true };
      } else if (d?.status === "checked") {
        toast.success("Substance check passed. Sent to operator queue for founder review.");
        onSubmittedForReview?.();
        return { submitted: true };
      } else if (d?.status === "superseded") {
        toast.error("The underlying doc changed. Please start a fresh edit.");
        onSubmittedForReview?.();
        return { submitted: false };
      } else {
        toast.warning("Checks found issues. See the notes below.");
        return { submitted: false };
      }
    } catch (e: any) {
      toast.error(e?.message || "Check failed.");
      return { submitted: false };
    } finally {
      setSubmitting(false);
    }
  }

  const hasProposal = proposalStatus === "checked" || proposalStatus === "approved" || proposalStatus === "escalated";

  useImperativeHandle(
    ref,
    () => ({
      submit: submitForCheck,
      getSnapshot: () => ({ dirty, hasIssues: issues.length > 0, hasProposal }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dirty, issues.length, hasProposal, proposalId, proposed, note],
  );


  const [escalatingIndex, setEscalatingIndex] = useState<number | null>(null);
  const [escalationText, setEscalationText] = useState("");
  const [escalationSubmitting, setEscalationSubmitting] = useState(false);


  async function submitEscalation(findingIndex: number, findingDetail: string) {
    if (!proposalId) return;
    const justification = escalationText.trim();
    if (justification.length < 10) {
      toast.error("Add a short justification (at least a sentence).");
      return;
    }
    setEscalationSubmitting(true);
    try {
      await persistDraft(true);
      const { error } = await supabase
        .from("doc_edit_proposals" as any)
        .update({
          status: "escalated",
          escalation_justification: justification,
          escalation_finding_index: findingIndex,
          escalation_finding_detail: findingDetail,
          escalated_at: new Date().toISOString(),
        })
        .eq("id", proposalId);
      if (error) throw error;
      setProposalStatus("escalated");
      setEscalatingIndex(null);
      setEscalationText("");
      toast.success("Sent for human review. An operator will look at this.");
      onSubmittedForReview?.();
    } catch (e: any) {
      toast.error(e?.message || "Escalation failed.");
    } finally {
      setEscalationSubmitting(false);
    }
  }

  function savedIndicator() {
    if (saveError) {
      return (
        <span className="text-[11px] flex items-center gap-1.5 flex-wrap">
          <span className="text-red-700 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" /> Not saved
          </span>
          <span className="text-muted-foreground">{saveError}</span>
          <button
            type="button"
            onClick={() => persistDraft(true)}
            className="underline text-foreground hover:no-underline"
          >
            Try again
          </button>
        </span>
      );
    }
    const unsaved = !savedRef.current || savedRef.current.proposed !== proposed || savedRef.current.note !== note;
    if (unsaved || autosaving) {
      return (
        <span className="text-[11px] text-muted-foreground flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Saving
        </span>
      );
    }
    if (lastSavedAt) {
      return (
        <span className="text-[11px] text-muted-foreground flex items-center gap-1">
          <Check className="h-3 w-3 text-green-700" /> Saved {lastSavedAt.toLocaleTimeString()}
        </span>
      );
    }
    return null;
  }

  return (
    <div ref={scrollRef} className="h-[75vh] overflow-auto pr-1 space-y-4">
      {proposalStatus === "escalated" && (
        <Alert>
          <ShieldQuestion className="h-4 w-4" />
          <AlertTitle>Sent for human review</AlertTitle>
          <AlertDescription>
            An operator will look at this proposal and either publish your version as written or explain why it cannot go through. You can keep editing in the meantime.
          </AlertDescription>
        </Alert>
      )}

      {/* Compact info + controls strip */}
      <Card className="p-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            <Info className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">
                Edit freely for clarity. Facts, numbers, links, headings, and defined terms are protected automatically.
              </p>
              <button
                type="button"
                onClick={toggleRules}
                className="mt-1 text-xs inline-flex items-center gap-1 leading-none text-foreground hover:underline"
                aria-expanded={rulesOpen}
              >
                {rulesOpen ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
                <span>See the rules</span>
              </button>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0 h-6">
            {savedIndicator()}
            <label
              htmlFor="raw-mode"
              className="flex items-center gap-2 cursor-pointer select-none leading-none"
            >
              <button
                type="button"
                id="raw-mode"
                role="switch"
                aria-checked={rawMode}
                aria-label="Raw markdown"
                data-state={rawMode ? "checked" : "unchecked"}
                onClick={toggleRawMode}
                onKeyDown={handleRawModeKeyDown}
                className="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border border-black/10 transition-colors data-[state=checked]:bg-[#1F3D2B] data-[state=unchecked]:bg-[#D6D3CB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3D2B] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F7F5F0]"
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none block h-4 w-4 rounded-full border border-black/10 bg-[#F7F5F0] shadow transition-transform ${
                    rawMode ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </button>
              <span className="text-xs leading-none">Raw markdown</span>
            </label>
            <div className="text-xs leading-none">
              {issues.length === 0 ? (
                <span className="text-green-700 inline-flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3 shrink-0" /> All checks pass
                </span>
              ) : (
                <span className="text-red-700 inline-flex items-center gap-1">
                  <AlertCircle className="h-3 w-3 shrink-0" /> {issues.length} {issues.length === 1 ? "thing" : "things"} to fix
                </span>
              )}
            </div>
          </div>
        </div>
        {rulesOpen && (
          <div className="mt-3 pt-3 border-t grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
            <div>
              <div className="font-semibold text-green-700 mb-1">Can change</div>
              <ul className="list-disc pl-4 space-y-0.5 text-muted-foreground">
                <li>Sentence structure</li>
                <li>Word choice</li>
                <li>Paragraph breaks</li>
                <li>Clarity, grammar, punctuation</li>
              </ul>
            </div>
            <div>
              <div className="font-semibold text-red-700 mb-1">Cannot change</div>
              <ul className="list-disc pl-4 space-y-0.5 text-muted-foreground">
                <li>Any number, dollar amount, date, form number, or threshold</li>
                <li>Any link or document reference</li>
                <li>Section headings or their order</li>
                <li>The closing disclaimer</li>
                <li>Capitalized defined terms (Contribution, Member Benefit, Convention, the Record, etc.)</li>
                <li>Anything that adds, removes, or reverses an instruction or claim</li>
              </ul>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-3">
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
          Your clarity edit
        </div>
        {rawMode ? (
          <Textarea
            value={proposed}
            onChange={(e) => setProposed(e.target.value)}
            className={`min-h-[45vh] font-mono text-xs ${
              issues.length > 0 ? "border-red-400" : ""
            }`}
          />
        ) : (
          <div className={`rounded-md ${issues.length > 0 ? "ring-1 ring-red-400" : ""}`}>
            <MarkdownBlockEditor
              value={proposed}
              originalMarkdown={base}
              onChange={setProposed}
            />
          </div>
        )}
        {!rawMode && (
          <p className="mt-2 text-[11px] text-muted-foreground">
            Headings and anything with a link are locked. Type normally in the open paragraphs.
            Bold with Cmd or Ctrl + B. Press Enter for a new paragraph.
          </p>
        )}
      </Card>

      {issues.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Checks to fix ({issues.length})</AlertTitle>
          <AlertDescription>
            <ul className="mt-2 space-y-1 text-xs">
              {issues.map((i, idx) => (
                <li key={idx}>
                  <Badge variant="destructive" className="mr-2">
                    {i.label}
                  </Badge>
                  {i.detail && <span className="text-muted-foreground">{i.detail}</span>}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {checkResult && typeof checkResult === "object" && (() => {
        const cr: any = checkResult || {};
        const mechanical: any[] = Array.isArray(cr.mechanical) ? cr.mechanical : [];
        const aiFindings: any[] = Array.isArray(cr?.ai?.findings) ? cr.ai.findings : [];
        const proofFindings: any[] = Array.isArray(cr?.proofread?.findings) ? cr.proofread.findings : [];
        return (
        <Card className="p-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
            Substance check
          </div>
          {cr.passed ? (
            <div className="space-y-1">
              <div className="text-sm text-green-700 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />{" "}
                {cr.auto_approved
                  ? `Auto-approved and published${cr.published_version ? ` as v${cr.published_version}` : ""}.`
                  : cr.publish_error
                    ? "Your edit passed every check. A house writing rule stopped it from publishing on its own, so the team will look at it."
                    : "Passed. This proposal is in the operator queue for founder review."}
              </div>
              {cr.publish_error && (
                <div className="text-xs text-muted-foreground">{cr.publish_error}</div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-sm text-red-700 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" /> Findings, please revise and resend.
              </div>
              <ul className="text-xs space-y-1">
                {mechanical.map((f: any, i: number) => (
                  <li key={`m${i}`}>
                    <Badge variant="outline" className="mr-2">
                      Mechanical
                    </Badge>
                    <span className="font-medium">{f?.category}:</span> {f?.detail}
                  </li>
                ))}
                {aiFindings.map((f: any, i: number) => (
                  <li key={`a${i}`} className="border-l-2 border-amber-300 pl-2 py-1">
                    <div>
                      <Badge variant="outline" className="mr-2">
                        Substance
                      </Badge>
                      {f?.detail}
                    </div>
                    {proposalStatus !== "escalated" && proposalId && (
                      <div className="mt-1">
                        {escalatingIndex === i ? (
                          <div className="space-y-2 bg-muted/40 rounded p-2">
                            <div className="text-[11px] text-muted-foreground">
                              Explain briefly why this is a clarity edit, not a change of substance. An operator will decide.
                            </div>
                            <Textarea
                              value={escalationText}
                              onChange={(e) => setEscalationText(e.target.value)}
                              placeholder="e.g. I only split a long sentence in two; the instruction is identical."
                              className="min-h-[70px] text-xs"
                            />
                            <div className="flex gap-2 justify-end">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setEscalatingIndex(null);
                                  setEscalationText("");
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => submitEscalation(i, f?.detail || "")}
                                disabled={escalationSubmitting}
                              >
                                {escalationSubmitting ? (
                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                ) : (
                                  <ShieldQuestion className="h-3 w-3 mr-1" />
                                )}
                                Send to human review
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="link"
                            className="h-auto p-0 text-xs"
                            onClick={() => {
                              setEscalatingIndex(i);
                              setEscalationText("");
                            }}
                          >
                            This is a clarity edit, request human review
                          </Button>
                        )}
                      </div>
                    )}
                  </li>
                ))}
                {proofFindings.map((f: any, i: number) => (
                  <li key={`p${i}`}>
                    <Badge variant="outline" className="mr-2">
                      Proofread
                    </Badge>
                    {f?.detail}
                  </li>
                ))}

              </ul>
            </div>
          )}
        </Card>
        );
      })()}

      {dirty && (
        <div className="space-y-2">
          <Input
            placeholder="Note about your edit (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex items-center gap-2 justify-between">
            {savedIndicator()}
            <Button onClick={submitForCheck} disabled={submitting || issues.length > 0}>
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-1" />
              )}
              Send rewrite
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Sends your rewrite to the checker. Send as often as you like. This does not finish the page.
          </p>
        </div>
      )}
    </div>
  );
});
